import { PERMISSIONS } from '@ibe/constants';
import { ErrorState, LoadingState } from '@ibe/ui';
import { useNavigate, useParams } from 'react-router-dom';

import { NotPermitted, PermissionGuard } from '../../../auth/permission-guard.js';
import { FormPage } from '../../../components/form-page/form-page.js';
import { errorTextFor } from '../../../lib/api/error-message.js';
import { useAppForm } from '../../../lib/forms/use-app-form.js';
import { exchangeRates } from '../api/endpoints.js';
import {
  buildCreateExchangeRatePayload,
  buildUpdateExchangeRatePayload,
} from '../api/mutations.js';
import {
  exchangeRateFormInput,
  type ExchangeRateDetail,
  type ExchangeRateFormInput,
} from '../api/schemas.js';
import { ExchangeRateFields } from '../forms/exchange-rate-fields.js';

const CREATE_DEFAULTS: ExchangeRateFormInput = {
  baseCurrency: '',
  quoteCurrency: '',
  rate: '',
  source: 'Manual',
  effectiveAt: '',
  expiresAt: '',
};

function defaultsFrom(rate: ExchangeRateDetail): ExchangeRateFormInput {
  return {
    baseCurrency: rate.baseCurrency,
    quoteCurrency: rate.quoteCurrency,
    rate: rate.rate,
    source: rate.source,
    effectiveAt: rate.effectiveAt ? rate.effectiveAt.split('T')[0] : '',
    expiresAt: rate.expiresAt ? rate.expiresAt.split('T')[0] : '',
  };
}

export function ExchangeRateFormPage(): React.JSX.Element {
  const exchangeRateId = useParams().exchangeRateId;
  const isEdit = exchangeRateId !== undefined;
  return (
    <PermissionGuard
      permission={isEdit ? PERMISSIONS.EXCHANGE_RATE_UPDATE : PERMISSIONS.EXCHANGE_RATE_CREATE}
      fallback={<NotPermitted />}
    >
      {isEdit ? <EditExchangeRate id={exchangeRateId} /> : <CreateExchangeRate />}
    </PermissionGuard>
  );
}

function CreateExchangeRate(): React.JSX.Element {
  const navigate = useNavigate();
  const create = exchangeRates.useCreate();
  const form = useAppForm<ExchangeRateFormInput>({
    schema: exchangeRateFormInput,
    defaultValues: CREATE_DEFAULTS,
    successMessage: 'Exchange rate created',
    onSubmit: async (values) => {
      const result = await create.mutateAsync(buildCreateExchangeRatePayload(values));
      void navigate(`/exchange-rates/${result.id}`);
    },
  });
  return (
    <FormPage
      title="New exchange rate"
      description="Add a rate used to convert between currencies."
      backTo="/exchange-rates"
      backLabel="Exchange rates"
      submitLabel="Create exchange rate"
      submitting={form.submitting}
      dirty={form.formState.isDirty}
      onSubmit={() => {
        void form.submit();
      }}
    >
      <ExchangeRateFields
        register={form.register}
        errors={form.formState.errors}
        control={form.control}
      />
    </FormPage>
  );
}

function EditExchangeRate({ id }: { readonly id: string }): React.JSX.Element {
  const query = exchangeRates.useItem(id);
  if (query.isPending) return <LoadingState label="Loading exchange rate" />;
  if (query.error !== null) {
    return (
      <ErrorState
        description={errorTextFor(query.error) ?? ''}
        onRetry={() => void query.refetch()}
      />
    );
  }
  return <EditExchangeRateForm id={id} rate={query.data} />;
}

function EditExchangeRateForm({
  id,
  rate,
}: {
  readonly id: string;
  readonly rate: ExchangeRateDetail;
}): React.JSX.Element {
  const navigate = useNavigate();
  const update = exchangeRates.useUpdate();
  const form = useAppForm<ExchangeRateFormInput>({
    schema: exchangeRateFormInput,
    defaultValues: defaultsFrom(rate),
    successMessage: 'Exchange rate updated',
    onSubmit: async (values) => {
      await update.mutateAsync({
        id,
        payload: buildUpdateExchangeRatePayload(values, rate.version),
      });
      void navigate(`/exchange-rates/${id}`);
    },
  });
  return (
    <FormPage
      title={`Edit ${rate.baseCurrency}/${rate.quoteCurrency}`}
      backTo={`/exchange-rates/${id}`}
      backLabel="Exchange rate"
      submitLabel="Save changes"
      submitting={form.submitting}
      dirty={form.formState.isDirty}
      onSubmit={() => {
        void form.submit();
      }}
    >
      <ExchangeRateFields
        register={form.register}
        errors={form.formState.errors}
        control={form.control}
      />
    </FormPage>
  );
}
